import { create } from 'zustand';
import { registerIdentityScopedReset } from './identity-scoped-reset';

/**
 * Shared "impersonation writes unlocked" toggle.
 *
 * Read on the request path (`api.ts`) to decide whether to send the
 * `x-impersonate-write` header, and toggled from the impersonation banner
 * once an admin confirms they want to write as the impersonated user.
 */
export interface ImpersonateWriteModeState {
  enabled: boolean;
  /** From `/api/me`'s `impersonation.active`, published by `UserControlsProvider`. `null` until it loads. */
  impersonating: boolean | null;
  enable: () => void;
  disable: () => void;
}

export const initialState: Pick<ImpersonateWriteModeState, 'enabled' | 'impersonating'> = {
  enabled: false,
  impersonating: null,
};

export const useImpersonateWriteModeStore = create<ImpersonateWriteModeState>((set) => ({
  ...initialState,
  enable: () => set({ enabled: true }),
  disable: () => set({ enabled: false }),
}));

/** The request headers this toggle adds: the one place the `x-impersonate-write` header is built. */
export function impersonateWriteHeaders(): Record<string, string> {
  return useImpersonateWriteModeStore.getState().enabled ? { 'x-impersonate-write': 'true' } : {};
}

/**
 * True only once `/api/me` reported an active impersonation. Unknown counts as
 * not impersonating: the server, not this flag, refuses writes.
 */
export function isImpersonating(): boolean {
  return useImpersonateWriteModeStore.getState().impersonating === true;
}

/** True while a read-only View as is active (impersonating, writes not unlocked): every write control is off. */
export function useWritesLocked(): boolean {
  return useImpersonateWriteModeStore(s => s.impersonating === true && !s.enabled);
}

// Approved behavior change (Blake decision, GTMOPS-2285 Track B U3): reset the
// write-unlock whenever the effective identity genuinely changes. The caller
// that reports the identity is `UserControlsProvider` — do not add one here.
registerIdentityScopedReset(() =>
  useImpersonateWriteModeStore.setState({ enabled: initialState.enabled, impersonating: initialState.impersonating }),
);
